import { useState, useEffect } from 'react'
import logger from '../common/logger'
import { processTrustMarkersCombination } from '../common/processTrustMarkersCombination'

/**
 * Client-side fetch for trust markers combinations (fallback when SSR data is unavailable).
 *
 * @param {string} combinationId - The ID of the trust markers combination to fetch (default: 'control')
 * @param {{ enabled?: boolean }} options - Pass enabled: false when SSR combination is provided
 * @returns {Object|null} The trust markers combination data or null if not loaded/error
 */
export const useTrustMarkers = (
  combinationId = 'control',
  { enabled = true } = {}
) => {
  const [trustMarkers, setTrustMarkers] = useState(null)
  const [isLoading, setIsLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)

      return undefined
    }

    let cancelled = false

    const fetchTrustMarkers = async () => {
      try {
        const response = await fetch('/api/trust-markers')

        if (!response.ok) {
          throw new Error(`Failed to fetch trust markers: ${response.status}`)
        }

        const data = await response.json()

        const combination = data.find((item) => {
          const match = item.id?.toLowerCase() === combinationId.toLowerCase()

          return match
        })

        if (!cancelled) {
          setTrustMarkers(processTrustMarkersCombination(combination))
        }
      } catch (error) {
        logger.error('useTrustMarkers: Error fetching trust markers', { error })
        if (!cancelled) {
          setTrustMarkers(null)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    setIsLoading(true)
    fetchTrustMarkers()

    return () => {
      cancelled = true
    }
  }, [combinationId, enabled])

  return { trustMarkers, isLoading }
}

export default useTrustMarkers
